// Refactored: provide a reusable AuthForm component (no window reload, no name collision with main App)
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import CloseIcon from '@mui/icons-material/Close';
import { signUpWithEmail, signInWithEmail, signInWithGoogle, completeGoogleRedirectSignIn, resetPassword } from '../firebase/authService';
import { trackLogin, trackSignUp } from '../utils/analytics';

export default function AuthForm({ onClose, initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;

    completeGoogleRedirectSignIn().then(result => {
      if (!active || result.noRedirect) return;
      if (!result.success) {
        setError(result.error);
        return;
      }

      trackLogin('google');
      setSuccess('Signed in with Google!');
      setTimeout(() => {
        if (active && onClose) onClose();
      }, 1500);
    });

    return () => { active = false; };
  }, [onClose]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (mode === 'reset') {
      setLoading(true);
      const result = await resetPassword(email);
      setLoading(false);

      if (result.success) {
        setSuccess('Password reset email sent! Check your inbox.');
        setTimeout(() => setMode('login'), 3000);
      } else {
        setError(result.error);
      }
      return;
    }

    if (!validatePassword(password)) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (mode === 'signup') {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (!displayName.trim()) {
        setError('Please enter your name');
        return;
      }
    }

    setLoading(true);

    let result;
    if (mode === 'signup') {
      result = await signUpWithEmail(email, password, displayName, marketingConsent);
    } else {
      result = await signInWithEmail(email, password);
    }

    setLoading(false);

    if (result.success) {
      if (mode === 'signup') { trackSignUp('email'); } else { trackLogin('email'); }
      setSuccess(mode === 'signup' ? 'Account created successfully!' : 'Signed in successfully!');
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } else {
      setError(result.error);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    const result = await signInWithGoogle();
    if (result.redirecting) return;
    setLoading(false);

    if (result.success) {
      trackLogin('google');
      setSuccess('Signed in with Google!');
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } else {
      setError(result.error);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 440,
        mx: 'auto',
        p: 4,
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        position: 'relative',
      }}
    >
      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#8a7060',
            '&:hover': { background: 'rgba(0,0,0,0.06)', color: '#2c2017' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      <Typography
        variant="h4"
        sx={{
          mb: 1,
          fontFamily: '"Space Grotesk", sans-serif',
          fontWeight: 800,
          textAlign: 'center',
          color: '#2c2017'
        }}
      >
        {mode === 'login' ? 'Welcome Back' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
      </Typography>
      <Typography
        sx={{
          mb: 3,
          fontFamily: '"Raleway", sans-serif',
          textAlign: 'center',
          color: '#4d4036',
          fontSize: 14
        }}
      >
        {mode === 'login' ? 'Sign in to continue learning' : mode === 'signup' ? 'Join Dumplingo today' : 'Enter your email to reset your password'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {mode === 'signup' && (
          <TextField
            fullWidth
            label="Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            sx={{ mb: 2 }}
            required
          />
        )}

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        {mode !== 'reset' && (
          <>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {mode === 'signup' && (
              <>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ mb: 2 }}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      sx={{ color: '#FF6123', '&.Mui-checked': { color: '#FF6123' } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 14 }}>
                      I agree to receive marketing emails
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </>
        )}

        {mode === 'login' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              onClick={() => setMode('reset')}
              sx={{
                textTransform: 'none',
                color: '#06b6d4',
                fontFamily: '"Raleway", sans-serif',
                '&:hover': { color: '#0891b2' }
              }}
            >
              Forgot password?
            </Button>
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            mb: 2,
            py: 1.5,
            background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
            fontFamily: '"Raleway", sans-serif',
            fontWeight: 700,
            textTransform: 'none',
            fontSize: 16,
            '&:hover': {
              background: 'linear-gradient(135deg, #5558e3 0%, #0891b2 100%)'
            }
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : mode === 'login' ? (
            'Sign In'
          ) : mode === 'signup' ? (
            'Create Account'
          ) : (
            'Send Reset Link'
          )}
        </Button>
      </form>

      {mode !== 'reset' && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography sx={{ color: '#4d4036', fontSize: 14, fontFamily: '"Raleway", sans-serif' }}>
              or continue with
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<GoogleIcon />}
            sx={{
              mb: 2,
              py: 1.5,
              borderColor: 'rgba(0,0,0,0.2)',
              color: '#2c2017',
              fontFamily: '"Raleway", sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: 'rgba(0,0,0,0.3)',
                background: 'rgba(0,0,0,0.02)'
              }
            }}
          >
            Google
          </Button>
        </>
      )}

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 14, color: '#4d4036' }}>
          {mode === 'login' ? "Don't have an account? " : mode === 'signup' ? 'Already have an account? ' : 'Remember your password? '}
          <Button
            onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
            sx={{
              textTransform: 'none',
              color: '#06b6d4',
              fontFamily: '"Raleway", sans-serif',
              fontWeight: 600,
              p: 0,
              minWidth: 'auto',
              '&:hover': { color: '#0891b2', background: 'transparent' }
            }}
          >
            {mode === 'login' ? 'Sign up' : mode === 'signup' ? 'Sign in' : 'Sign in'}
          </Button>
        </Typography>
      </Box>
    </Box>
  );
}